/**
 * Tells a slide body whether its hero entry may run (CEE-1402).
 *
 * `entriesReady` flips true after the deck push settles (~80ms gap). `shouldPlayEntry` is false
 * on revisit within the session (replay-once). Layouts in later PRs read this and toggle
 * `.cs-motion-enter.is-in`; the foundation only owns the signal.
 */
import { createContext, useContext, useMemo, type CSSProperties, type ReactNode } from 'react';

export interface SlideEntryValue {
  /** Deck push finished (or there was no push); entries may begin. */
  entriesReady: boolean;
  /** First visit this session — play the hero entrance. */
  shouldPlayEntry: boolean;
  slideNumber: number;
}

const SlideEntryContext = createContext<SlideEntryValue>({
  entriesReady: true,
  shouldPlayEntry: false,
  slideNumber: 0,
});

export function SlideEntryProvider({
  entriesReady,
  shouldPlayEntry,
  slideNumber,
  children,
}: SlideEntryValue & { children: ReactNode }) {
  const value = useMemo(
    () => ({ entriesReady, shouldPlayEntry, slideNumber }),
    [entriesReady, shouldPlayEntry, slideNumber],
  );
  return <SlideEntryContext.Provider value={value}>{children}</SlideEntryContext.Provider>;
}

export function useSlideEntry(): SlideEntryValue {
  return useContext(SlideEntryContext);
}

/**
 * Stagger index as a CSS variable for `.cs-motion-enter` (cap at 5 — beyond that, animate as one
 * block by passing the same index / omitting stagger).
 *
 * Cast: React's `CSSProperties` has no `--*` keys; custom props are valid at runtime.
 */
export function entryStaggerStyle(index: number): CSSProperties {
  return { ['--cs-motion-i']: Math.max(0, Math.min(index, 4)) } as CSSProperties;
}
